/**
 * 解析の単一入口（U154）。呼び出し側は走査窓のコード列だけ渡す。
 * 座標は窓先頭からの相対 → 分子座標へ戻す。
 */

import type { Interval, Topology } from '../sequence/interval';
import { searchCodes, type PatternHit } from './patternSearch';
import {
  digestFragments,
  scanRestrictionCuts,
  silentSiteSuggestions,
  type RestrictionCut,
  type RestrictionFragment,
} from './restrictionScan';
import type { RestrictionEnzyme } from './restrictionEnzymes';
import { findOrfs, type PredictedOrf } from './translation';
import {
  DEFAULT_PRIMER_SETTINGS,
  designPrimerPair,
  type DesignedPrimer,
  type PrimerDesignSettings,
} from './primer';
import { CRISPR_NUCLEASES, findCrisprGuides, type CrisprGuide, type CrisprNuclease } from './crispr';

export type AnalysisKind = 'search' | 'restriction' | 'orf' | 'primer' | 'crispr';

export interface AnalysisRequest {
  kind: AnalysisKind;
  genomeLength: number;
  topology: Topology;
  /** 分子座標の走査窓 */
  interval: Interval;
  search?: { pattern: string; bothStrands?: boolean; limit?: number };
  restriction?: { enzymeIds: string[]; extraEnzymes?: RestrictionEnzyme[] };
  primer?: Partial<PrimerDesignSettings>;
  crispr?: { nucleaseId?: string; custom?: CrisprNuclease; limit?: number };
}

export interface AnalysisResult {
  kind: AnalysisKind;
  hits: PatternHit[];
  cuts: RestrictionCut[];
  fragments: RestrictionFragment[];
  silent: RestrictionEnzyme[];
  orfs: PredictedOrf[];
  primer: { forward: DesignedPrimer | null; reverse: DesignedPrimer | null };
  guides: CrisprGuide[];
}

const EMPTY: AnalysisResult = {
  kind: 'search',
  hits: [],
  cuts: [],
  fragments: [],
  silent: [],
  orfs: [],
  primer: { forward: null, reverse: null },
  guides: [],
};

export function remapLocalInterval(local: Interval, window: Interval, genomeLength: number): Interval {
  let start = window.start + local.start;
  let end = window.start + local.end;
  if (start >= genomeLength && end >= genomeLength) {
    start -= genomeLength;
    end -= genomeLength;
  }
  return { start, end };
}

export function runAnalysisOnCodes(codes: Uint8Array, request: AnalysisRequest): AnalysisResult {
  const fullMolecule =
    request.interval.start === 0 && request.interval.end >= request.genomeLength && codes.length === request.genomeLength;
  const scanInterval: Interval = { start: 0, end: codes.length };
  const scanTopology: Topology = fullMolecule ? request.topology : 'linear';

  const remapHit = <T extends { start: number; end: number }>(item: T): T => {
    if (fullMolecule) return item;
    const mapped = remapLocalInterval({ start: item.start, end: item.end }, request.interval, request.genomeLength);
    return { ...item, start: mapped.start, end: mapped.end };
  };

  const result: AnalysisResult = { ...EMPTY, kind: request.kind };

  if (request.kind === 'search' && request.search?.pattern) {
    result.hits = searchCodes(codes, {
      genomeLength: codes.length,
      topology: scanTopology,
      interval: scanInterval,
      pattern: request.search.pattern,
      bothStrands: request.search.bothStrands,
      limit: request.search.limit,
    }).map(remapHit);
    return result;
  }

  if (request.kind === 'restriction' && request.restriction) {
    const localCuts = scanRestrictionCuts(codes, {
      genomeLength: codes.length,
      topology: scanTopology,
      interval: scanInterval,
      enzymeIds: request.restriction.enzymeIds,
      extraEnzymes: request.restriction.extraEnzymes,
    });
    result.cuts = localCuts.map((cut) => {
      const mapped = remapHit(cut);
      let cutPos = fullMolecule ? cut.cut : request.interval.start + cut.cut;
      if (cutPos >= request.genomeLength) cutPos -= request.genomeLength;
      return { ...mapped, cut: cutPos };
    });
    result.fragments = digestFragments(result.cuts, request.genomeLength, request.topology);
    result.silent = silentSiteSuggestions(codes, {
      genomeLength: codes.length,
      topology: scanTopology,
      interval: scanInterval,
      selectedIds: request.restriction.enzymeIds,
    });
    return result;
  }

  if (request.kind === 'orf') {
    result.orfs = findOrfs(codes, {
      genomeLength: codes.length,
      topology: scanTopology,
    }).map(remapHit);
    return result;
  }

  if (request.kind === 'primer') {
    const pair = designPrimerPair(codes, { ...DEFAULT_PRIMER_SETTINGS, ...request.primer });
    result.primer = {
      forward: pair.forward ? remapHit(pair.forward) : null,
      reverse: pair.reverse ? remapHit(pair.reverse) : null,
    };
    return result;
  }

  if (request.kind === 'crispr') {
    const nuclease =
      request.crispr?.custom ??
      CRISPR_NUCLEASES.find((item) => item.id === request.crispr?.nucleaseId) ??
      CRISPR_NUCLEASES[0];
    result.guides = findCrisprGuides(codes, {
      genomeLength: codes.length,
      topology: scanTopology,
      interval: scanInterval,
      nuclease,
      limit: request.crispr?.limit,
    }).map(remapHit);
    return result;
  }

  return result;
}
